import React, { useState } from "react";
import FilterBar from "components/FilterBar";
import DSBox from "../../system-design/DSBox";
import DSTypography from "../../system-design/DSTypography";
import DSCircularProgress from "../../system-design/DSCircularProgress";
import { DSBaseTable, DSTableCell } from "../../system-design/Table";
import { Column } from "../../system-design/Table/DSBaseTable/components/DSBaseTable";
import useCalendarEventsList from "../hooks/useCalendarEventsList";
import CalendarEventDialog, { WithRequiredId } from "./CalendarEventDialog";
import { CalendarEvent } from "../../../api/calendarEvent";

const CalendarEventsList = () => {
  const {
    data: calendarEvents,
    fetchNextPage,
    isLoading,
    handleTextSearch,
    columns,
  } = useCalendarEventsList();

  const [chosenCalendarEvent, setChosenCalendarEvent] =
    useState<WithRequiredId<CalendarEvent> | null>(null);
  const [searchInput, setSearchInput] = useState("");

  const formatDate = (date: CalendarEvent["date"]) => {
    return `${date.year}-${String(date.month).padStart(2, "0")}-${String(date.day).padStart(2, "0")}`;
  };

  const formatTypes = (types: string[]) => {
    if (!types || types.length === 0) return "None";
    return types.slice(0, 2).join(", ") + (types.length > 2 ? "..." : "");
  };

  const getCellValue = (field: string, calendarEvent: CalendarEvent) => {
    switch (field) {
      case "date":
        return formatDate(calendarEvent.date);
      case "country":
        return calendarEvent?.country?.name;
      case "type":
        return formatTypes(calendarEvent.type);
      default:
        return String(calendarEvent[field as keyof CalendarEvent]);
    }
  };

  const buildTableRows = (
    calendarEvent: CalendarEvent,
    columns: Column<CalendarEvent>[],
  ) => (
    <>
      {columns.map((col) => (
        <DSTableCell
          key={col.field as string}
          style={{
            width: col.size || `${100 / columns?.length}%`,
            whiteSpace: "nowrap",
          }}
        >
          {getCellValue(col.field as string, calendarEvent)}
        </DSTableCell>
      ))}
    </>
  );

  const handleRowClick = (calendarEvent: CalendarEvent) => {
    setChosenCalendarEvent(calendarEvent);
  };

  const Content = () => {
    if (isLoading) {
      return (
        <DSBox
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <DSCircularProgress />
        </DSBox>
      );
    }

    if (!calendarEvents) {
      return (
        <DSTypography variant="body1" textAlign="center">
          <p>No Calendar Events found</p>
        </DSTypography>
      );
    }

    return (
      <DSBaseTable
        columns={columns}
        onPressRow={handleRowClick}
        rowBuilder={buildTableRows}
        fetchMore={fetchNextPage}
        data={calendarEvents}
      />
    );
  };

  return (
    <>
      <FilterBar
        search={{
          value: searchInput,
          onChange: (value) => {
            setSearchInput(value);
            handleTextSearch(value);
          },
          placeholder: "Search Calendar Events",
        }}
      />
      <Content />
      {chosenCalendarEvent && (
        <CalendarEventDialog
          calendarEvent={chosenCalendarEvent}
          open={!!chosenCalendarEvent}
          onClose={() => {
            setChosenCalendarEvent(null);
          }}
        />
      )}
    </>
  );
};

export default CalendarEventsList;
